/**
 * Compact file-style card shown for `update_artifact` tool parts (and for
 * older `create_artifact` parts that have been superseded by a newer one).
 *
 * Treats the artifact like an attached file in the chat timeline — the
 * artifact title is the headline, the subtitle is fixed ("Edited document")
 * regardless of in-flight state so the row doesn't visibly mutate while the
 * model writes. A trailing arrow communicates that clicking opens the
 * canvas at the latest revision.
 *
 * @module components/artifacts/artifact-update-badge
 */

import type { ToolUIPart } from "ai";
import { ArrowUpRight, FileText, Loader2 } from "lucide-react";
import { useRef } from "react";
import { useArtifactStore } from "@/store/artifact-store";
import type { ArtifactKind, ArtifactToolResult } from "@taqat/core/types";

interface ArtifactUpdateBadgeProps {
  /** Tool part being rendered — output present once the update completes. */
  part: ToolUIPart;
}

/**
 * File-card affordance summarizing one artifact edit in the chat timeline.
 *
 * The title and subtitle are stable across streaming → completed states so
 * the row doesn't shift or relabel as the model finishes writing. The icon
 * swaps to a spinner during streaming so the user still sees progress.
 */
export function ArtifactUpdateBadge({ part }: ArtifactUpdateBadgeProps) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const showArtifact = useArtifactStore((s) => s.showArtifact);
  const setArtifact = useArtifactStore((s) => s.setArtifact);
  const liveArtifact = useArtifactStore((s) => s.artifact);

  const input = (part.input ?? {}) as { title?: string; id?: string };
  const output = (part.output ?? null) as ArtifactToolResult | null;

  const id = output?.id || input.id || "";
  const title = output?.title || input.title || liveArtifact.title || "Document";
  const kind: ArtifactKind = output?.kind || "document";
  const isStreaming =
    !output && !!id && liveArtifact.id === id && liveArtifact.status === "streaming";

  /**
   * Open the canvas with the latest known body, anchored at this card so
   * the open animation has an origin matching where the user clicked.
   */
  function handleOpen() {
    if (!id) return;
    const rect = ref.current?.getBoundingClientRect();
    setArtifact({
      id,
      title,
      kind,
      content: liveArtifact.id === id ? liveArtifact.content : output?.content || "",
      status: "idle",
    });
    showArtifact(
      rect
        ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
        : undefined,
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleOpen}
      disabled={!id}
      className="group my-2 flex w-full items-center gap-3 rounded-xl border bg-background px-3 py-2.5 text-left transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {/* Icon tile — left. Spinner during streaming, file icon when idle. */}
      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {isStreaming ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <FileText className="size-4" />
        )}
      </div>

      {/* Title + fixed subtitle — middle, truncates on overflow. */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{title}</div>
        <div className="truncate text-xs text-muted-foreground">Edited document</div>
      </div>

      {/**
       * Open affordance — text + icon, transparent. The whole card is the
       * click target so this is purely visual; rendering it as a `<span>`
       * (not nested `<button>`) keeps the markup valid and the entire row
       * accessible as one button.
       */}
      <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-transparent px-2 py-1 text-xs font-medium text-muted-foreground transition-colors group-hover:border-border group-hover:text-foreground">
        Open
        <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}
